import React, { memo, useCallback, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import './MatchQuestion.css';

// Create audio element with public URL
const clickAudio = new Audio('/sounds/click.mp3');

// Preload the audio file
clickAudio.load();

/**
 * A draggable match item that can be connected to items in the opposite column
 */
const MatchItem = memo(({ 
  item, 
  isLeft, 
  isMatched, 
  isSelected, 
  isDragging = false,
  isOver = false,
  pairClass, 
  onDragStart, 
  onDrop, 
  onRemove, 
  onSelect,
  draggedItem,
  itemNumber,
  getItemClass,
  className,
}) => {
  const itemId = isLeft ? `left-${item.id}` : `right-${item.id}`;
  const itemLabel = item.text || (isLeft ? 'Item' : 'Match');
  
  // Handle drop on the match item
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDrop) {
      onDrop(e, itemId);
    }
  }, [onDrop, itemId]);
  
  const handleDragStart = useCallback((e) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', itemId);
    onDragStart(itemId);
    
    // Add visual feedback
    e.currentTarget.classList.add('dragging');
  }, [itemId, onDragStart]);

  const handleDragEnd = useCallback((e) => {
    onDragStart(null);
    if (e.currentTarget) {
      e.currentTarget.classList.remove('dragging');
    }
  }, [onDragStart]);

  const handleRemove = useCallback((e) => {
    e.stopPropagation();
    onRemove(e, itemId);
  }, [itemId, onRemove]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    // Only process keyboard events for non-matched items
    if (isMatched) return;
    
    // Handle Enter and Space for selection
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(item);
      return;
    }
    
    // Handle arrow keys for keyboard navigation
    const arrowKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
    if (!arrowKeys.includes(e.key)) return;
    
    e.preventDefault();
    const container = e.currentTarget.closest('.match-column');
    if (!container) return;
    
    const items = Array.from(container.querySelectorAll('.match-item:not([disabled])'));
    const currentIndex = items.indexOf(e.currentTarget);
    
    if (currentIndex >= 0) {
      let nextIndex = currentIndex;
      
      if (e.key === 'ArrowDown' || (isLeft && e.key === 'ArrowRight') || (!isLeft && e.key === 'ArrowLeft')) {
        nextIndex = (currentIndex + 1) % items.length;
      } else if (e.key === 'ArrowUp' || (isLeft && e.key === 'ArrowLeft') || (!isLeft && e.key === 'ArrowRight')) {
        nextIndex = (currentIndex - 1 + items.length) % items.length;
      }
      
      if (nextIndex !== currentIndex && items[nextIndex]) {
        items[nextIndex].focus();
      }
    }
  }, [isLeft, item, onSelect, isMatched]);
  
  // Get the class name for the button element
  const getButtonClassName = useCallback(() => {
    const classes = ['match-item'];
    if (isMatched) classes.push('matched');
    if (isDragging) classes.push('dragging');
    if (isOver) classes.push('drag-over');
    if (pairClass) classes.push(pairClass);
    return classes.filter(Boolean).join(' ');
  }, [isMatched, isDragging, isOver, pairClass]);
  
  // Handle click on the match item
  const handleClick = useCallback((e) => {
    if (isMatched) return;
    if (onSelect) {
      onSelect(e, item);
    }
  }, [isMatched, item, onSelect]);

  if (isMatched) {

    // In the MatchItem component, update the button's className:
    const buttonClassName = `${getButtonClassName()} ${className || ''}`.trim();
    
    return (
      <div className="matched-item-container">
        <button
          className={buttonClassName}
          onClick={handleClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClick(e);
            }
          }}
          aria-label={`Matched item: ${item.text}`}
          type="button"
        >
          <div className="match-item-content">
            {item.image && (
              <img 
                src={item.image} 
                alt="" 
                className="match-option-image" 
                aria-hidden="true"
              />
            )}
            <span id={`${itemId}-text`} className="match-option-text">
              {item.text}
            </span>
          </div>
        </button>
        <button
          type="button"
          className="remove-match"
          onClick={(e) => {
            e.stopPropagation();
            handleRemove(e);
            clickAudio.play();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation();
              handleRemove(e);
            }
          }}
          aria-label={`Remove match for ${itemLabel}`}
        >
          <span className="remove-icon" aria-hidden="true">⨉</span>
          <span className="sr-only">Remove match</span>
        </button>
      </div>
    );
  }

  // Get the class name for the button element
  const buttonClassName = getButtonClassName();

  return (
    <button
      className={buttonClassName}
      disabled={isMatched}
      draggable={!isMatched}
      onDragStart={!isMatched ? handleDragStart : undefined}
      onDragEnd={handleDragEnd}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={`${itemLabel}${isMatched ? ' (matched)' : ''}`}
      aria-pressed={isMatched}
      type="button"
    >
      <div className="match-item-content">
        {item.image && (
          <img 
            src={item.image} 
            alt="" 
            className="match-option-image" 
            aria-hidden="true"
          />
        )}
        <span id={`${itemId}-text`} className="match-option-text">
          {item.text}
        </span>
      </div>
    </button>
  );
});

// Define prop types for the MatchItem component
MatchItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    text: PropTypes.string.isRequired,
    image: PropTypes.string,
  }).isRequired,
  isLeft: PropTypes.bool.isRequired,
  isMatched: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool,
  isDragging: PropTypes.bool,
  isOver: PropTypes.bool,
  onSelect: PropTypes.func, // Made optional
  pairClass: PropTypes.string,
  onDragStart: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  draggedItem: PropTypes.string,
  itemNumber: PropTypes.number,
  getItemClass: PropTypes.func,
  className: PropTypes.string,
};

// Fisher-Yates shuffle algorithm
const shuffleArray = (array) => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

const MatchQuestion = React.memo(({ 
  question, 
  selectedAnswers = [], 
  onMatch, 
  correctAnswers = [],
  isSubmitted = false
}) => {
  // Shuffle left and right options when the question changes
  const [shuffledLeft, shuffledRight] = useMemo(() => {
    try {
      if (!question?.leftOptions || !question?.rightOptions) {
        console.warn('Missing left or right options in question');
        return [[], []];
      }
      return [
        shuffleArray([...question.leftOptions]),
        shuffleArray([...question.rightOptions])
      ];
    } catch (error) {
      console.error('Error shuffling options:', error);
      return [[], []];
    }
  }, [question]); // Only depend on the full question object

  // Convert selectedAnswers to connections format if it's an array
  const initialConnections = useMemo(() => {
    const connections = {};
    const colors = {};
    
    if (Array.isArray(selectedAnswers) && selectedAnswers.length > 0) {
      selectedAnswers.forEach(({ leftId, rightId }, index) => {
        try {
          if (leftId === undefined || rightId === undefined) {
            console.warn('Invalid match data:', { leftId, rightId });
            return;
          }
          const leftKey = `left-${leftId}`;
          const rightKey = `right-${rightId}`;
          const colorClass = `pair-${(index % 5) + 1}`;
          
          connections[leftKey] = rightKey;
          connections[rightKey] = leftKey;
          colors[leftKey] = colorClass;
          colors[rightKey] = colorClass;
        } catch (error) {
          console.error('Error processing match:', { leftId, rightId, error });
        }
      });
    }
    
    return { connections, colors };
  }, [selectedAnswers]); // Only depend on selectedAnswers
  
  // Helper function to check if a match is correct
  const isMatchCorrect = React.useCallback((leftId, rightId) => {
    if (!isSubmitted || !Array.isArray(correctAnswers) || correctAnswers.length === 0) {
      return false;
    }
    
    try {
      // Extract the numeric IDs from the element IDs
      const leftNumId = leftId.startsWith('left-') ? leftId.replace('left-', '') : leftId;
      const rightNumId = rightId.startsWith('right-') ? rightId.replace('right-', '') : rightId;
      
      if (!leftNumId || !rightNumId) return false;
      
      return correctAnswers.some(match => 
        (String(match.leftId) === String(leftNumId) && String(match.rightId) === String(rightNumId)) ||
        (String(match.leftId) === String(rightNumId) && String(match.rightId) === String(leftNumId))
      );
    } catch (error) {
      console.error('Error in isMatchCorrect:', { leftId, rightId, error });
      return false;
    }
  }, [correctAnswers, isSubmitted]);
  
  // Helper function to check if an item is part of a correct match
  const isItemInCorrectMatch = React.useCallback((itemId) => {
    if (!isSubmitted || !correctAnswers || !Array.isArray(correctAnswers)) return false;
    
    // Extract the numeric ID from the element ID
    const id = itemId.startsWith('left-') || itemId.startsWith('right-') 
      ? itemId.replace(/^(left|right)-/, '') 
      : itemId;
    
    return correctAnswers.some(match => 
      String(match.leftId) === String(id) || 
      String(match.rightId) === String(id)
    );
  }, [correctAnswers, isSubmitted]);

  // Declare state variables first
  const [connections, setConnections] = useState(initialConnections.connections);
  const [pairColors, setPairColors] = useState(initialConnections.colors);
  const [draggedItem, setDraggedItem] = useState(null);
  
  // Memoize isMatchedItemCorrect to prevent unnecessary re-renders
  const isMatchedItemCorrect = useMemo(() => {
    const cache = new Map();
    
    return (itemId, isLeft, otherItemId) => {
      if (!isSubmitted || !Array.isArray(correctAnswers) || correctAnswers.length === 0) {
        return false;
      }
      
      const cacheKey = `${itemId}-${isLeft}-${otherItemId}`;
      if (cache.has(cacheKey)) {
        return cache.get(cacheKey);
      }
      
      // Ensure we're passing the correct IDs based on their position
      const leftId = isLeft ? itemId : otherItemId;
      const rightId = isLeft ? otherItemId : itemId;
      
      // Make sure we have valid IDs to compare
      if (!leftId || !rightId) {
        cache.set(cacheKey, false);
        return false;
      }
      
      const result = isMatchCorrect(leftId, rightId);
      cache.set(cacheKey, result);
      return result;
    };
  }, [isSubmitted, correctAnswers, isMatchCorrect]);

  // Memoize the getItemClass function to prevent unnecessary re-renders
  const getItemClass = useMemo(() => {
    const classCache = new Map();
    
    return (itemId, isLeft) => {
      const cacheKey = `${itemId}-${isLeft}-${isSubmitted}-${draggedItem}`;
      
      if (classCache.has(cacheKey)) {
        return classCache.get(cacheKey);
      }
      
      const className = ['match-item'];
      const isMatched = !!connections[itemId];
      const pairClass = pairColors[itemId] || '';
      
      if (isMatched) {
        className.push('matched');
        if (pairClass) {
          className.push(pairClass);
        }
        
        if (isSubmitted) {
          const isCorrect = isMatchedItemCorrect(itemId, isLeft, connections[itemId]);
          className.push(isCorrect ? 'correct' : 'incorrect');
        }
      } else if (isSubmitted && isItemInCorrectMatch(itemId)) {
        className.push('should-match');
      }
      
      if (draggedItem === itemId) {
        className.push('dragging');
      }
      
      const result = className.filter(Boolean).join(' ');
      classCache.set(cacheKey, result);
      return result;
    };
  }, [connections, pairColors, isSubmitted, isMatchedItemCorrect, isItemInCorrectMatch, draggedItem]);

  const updateConnections = useCallback((newConnections, newPairColors = {}) => {
    try {
      // Validate inputs
      if (!newConnections || typeof newConnections !== 'object') {
        console.error('Invalid connections object:', newConnections);
        return;
      }
      
      setConnections(prevConnections => {
        // Only update if connections have actually changed
        if (JSON.stringify(prevConnections) === JSON.stringify(newConnections)) {
          return prevConnections;
        }
        return newConnections;
      });
      
      setPairColors(prevColors => {
        // Only update if colors have actually changed
        if (JSON.stringify(prevColors) === JSON.stringify(newPairColors)) {
          return prevColors;
        }
        return newPairColors;
      });
      
      // Convert connections to array format for parent component
      const matches = [];
      const processed = new Set();
      
      Object.entries(newConnections).forEach(([key, value]) => {
        try {
          if (key.startsWith('left-') && !processed.has(key)) {
            const leftId = key.replace('left-', '');
            const rightId = value?.replace('right-', '');
            
            if (leftId && rightId) {
              matches.push({ leftId, rightId });
              processed.add(key);
              processed.add(value);
            }
          }
        } catch (error) {
          console.error('Error processing connection:', { key, value, error });
        }
      });
      
      if (matches.length > 0) {
        onMatch(matches);
      }
    } catch (error) {
      console.error('Error in updateConnections:', error);
    }
  }, [onMatch]);

  const handleDrop = useCallback((e, targetId) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('handleDrop - draggedItem:', draggedItem, 'targetId:', targetId);
    
    if (!draggedItem || !targetId) {
      console.log('Missing draggedItem or targetId');
      return;
    }
    
    // Don't allow dropping on the same item
    if (draggedItem === targetId) {
      console.log('Cannot drop on self');
      return;
    }
    
    const isLeftDragged = draggedItem.startsWith('left-');
    const isLeftTarget = targetId.startsWith('left-');
    
    // Only allow drag between left and right columns
    if (isLeftDragged === isLeftTarget) {
      console.log('Can only drag between left and right columns');
      return;
    }
    
    const leftId = isLeftDragged ? draggedItem : targetId.replace('right-', 'left-');
    const rightId = isLeftDragged ? targetId : draggedItem.replace('left-', 'right-');
    
    console.log('leftId:', leftId, 'rightId:', rightId);
    
    // Check if either item is already matched
    const isLeftMatched = connections[leftId] !== undefined;
    const isRightMatched = connections[rightId] !== undefined;
    
    console.log('isLeftMatched:', isLeftMatched, 'isRightMatched:', isRightMatched);
    
    if (isLeftMatched || isRightMatched) {
      console.log('One or both items already matched');
      return;
    }
    
    // Create a new connection with the next available color
    const newConnections = { ...connections };
    const newPairColors = { ...pairColors };
    
    // Find the next available color index (1-5)
    const usedColors = new Set(Object.values(newPairColors));
    let nextColor = 1;
    while (usedColors.has(`pair-${nextColor}`) && nextColor <= 5) {
      nextColor++;
    }
    if (nextColor > 5) nextColor = 1; // Reset if we've used all colors
    
    const newPairClass = `pair-${nextColor}`;
    
    console.log('handleDrop - Setting new pair class:', {
      leftId,
      rightId,
      newPairClass,
      currentPairColors: newPairColors,
      usedColors: Array.from(usedColors)
    });
    
    // Add the new connection with color
    newConnections[leftId] = rightId;
    newConnections[rightId] = leftId;
    newPairColors[leftId] = newPairClass;
    newPairColors[rightId] = newPairClass;
    
    console.log('New connections:', newConnections);
    console.log('New pair colors:', newPairColors);
    
    // Update the connections and pair colors
    setConnections(newConnections);
    setPairColors(newPairColors);
    clickAudio.play();
    
    // Convert connections to array format for parent component
    const matches = [];
    const processed = new Set();
    
    Object.entries(newConnections).forEach(([key, value]) => {
      if (key.startsWith('left-') && !processed.has(key)) {
        matches.push({
          leftId: key.replace('left-', ''),
          rightId: value.replace('right-', '')
        });
        processed.add(key);
        processed.add(value);
      }
    });
    
    console.log('Sending to onMatch:', matches);
    onMatch(matches);
    setDraggedItem(null);
  }, [connections, draggedItem, pairColors, onMatch]);

  const removeConnection = useCallback(async (e, itemId) => {
    e.stopPropagation();
    const connectedId = connections[itemId];
    if (!connectedId) return;
    
    const newConnections = { ...connections };
    const newPairColors = { ...pairColors };
    
    delete newConnections[itemId];
    delete newConnections[connectedId];
    delete newPairColors[itemId];
    delete newPairColors[connectedId];
    
    // Update local state immediately for better UX
    setConnections(newConnections);
    setPairColors(newPairColors);
    
    // Then update the parent component
    updateConnections(newConnections, newPairColors);
  }, [connections, pairColors, updateConnections]);

  // Reset connections when question changes
  useEffect(() => {
    try {
      if (!initialConnections || typeof initialConnections !== 'object') {
        console.error('Invalid initialConnections:', initialConnections);
        return;
      }
      
      setConnections(prev => {
        const newConnections = initialConnections.connections || {};
        return JSON.stringify(prev) === JSON.stringify(newConnections) 
          ? prev 
          : newConnections;
      });
      
      setPairColors(prev => {
        const newColors = initialConnections.colors || {};
        return JSON.stringify(prev) === JSON.stringify(newColors)
          ? prev
          : newColors;
      });
    } catch (error) {
      console.error('Error resetting connections:', error);
    }
  }, [initialConnections]);

  const handleDragStart = useCallback((itemId) => {
    try {
      setDraggedItem(prev => {
        // Only update if the dragged item has changed
        return prev === itemId ? prev : itemId;
      });
    } catch (error) {
      console.error('Error in handleDragStart:', error);
    }
  }, []);

  return (
    <div className="match-question">
      <div className={`match-container ${isSubmitted ? 'submitted' : ''}`}>
        <div className="match-areas">
        {/* Left Column */}
        <div className="match-column left-column">
          <h3>Column A</h3>
          <ul className="match-items">
            {shuffledLeft.map((item) => {
              const itemId = `left-${item.id}`;
              const isMatched = !!connections[itemId];
              const pairClass = pairColors[itemId] || '';
              
              console.log('Rendering left MatchItem:', {
                itemId,
                isMatched,
                pairClass,
                draggedItem,
                hasPairClass: !!pairClass
              });
              
              return (
                <li key={itemId} className="match-item-container">
                  <MatchItem
                    item={item}
                    isLeft={true}
                    isMatched={isMatched}
                    isSelected={draggedItem === itemId}
                    pairClass={pairClass}
                    onDragStart={handleDragStart}
                    onDrop={handleDrop}
                    onRemove={removeConnection}
                    onSelect={() => {}} // Add empty function to satisfy prop types
                    draggedItem={draggedItem}
                    itemNumber={0}
                    getItemClass={getItemClass}
                    className={getItemClass(itemId, true)}
                  />
                </li>
              );
            })}
          </ul>
        </div>
        
        {/* Right Column */}
        <div className="match-column right-column">
          <h3>Column B</h3>
          <ul className="match-items">
            {shuffledRight.map((item) => {
              const itemId = `right-${item.id}`;
              const isMatched = !!connections[itemId];
              const pairClass = pairColors[itemId] || '';
              
              console.log('Rendering right MatchItem:', {
                itemId,
                isMatched,
                pairClass,
                draggedItem,
                hasPairClass: !!pairClass
              });
              
              return (
                <li key={itemId} className="match-item-container">
                  <MatchItem
                    item={item}
                    isLeft={false}
                    isMatched={isMatched}
                    isSelected={draggedItem === itemId}
                    pairClass={pairClass}
                    onDragStart={handleDragStart}
                    onDrop={handleDrop}
                    onRemove={removeConnection}
                    onSelect={() => {}} // Add empty function to satisfy prop types
                    draggedItem={draggedItem}
                    itemNumber={0}
                    getItemClass={getItemClass}
                    className={getItemClass(itemId, false)}
                  />
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      </div>
    </div>
  );
});

// Add display name for better debugging
MatchQuestion.displayName = 'MatchQuestion';

MatchQuestion.propTypes = {
  question: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    text: PropTypes.string,
    image: PropTypes.string,
    leftOptions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        text: PropTypes.string,
        image: PropTypes.string
      })
    ).isRequired,
    rightOptions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        text: PropTypes.string,
        image: PropTypes.string
      })
    ).isRequired
  }).isRequired,
  selectedAnswers: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({
      leftId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      rightId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
    })),
    PropTypes.object
  ]),
  onMatch: PropTypes.func.isRequired,
  correctAnswers: PropTypes.arrayOf(
    PropTypes.shape({
      leftId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      rightId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
    })
  ),
  isSubmitted: PropTypes.bool
};

const arePropsEqual = (prevProps, nextProps) => {
  return (
    prevProps.isSubmitted === nextProps.isSubmitted &&
    JSON.stringify(prevProps.selectedAnswers) === JSON.stringify(nextProps.selectedAnswers) &&
    JSON.stringify(prevProps.correctAnswers) === JSON.stringify(nextProps.correctAnswers) &&
    prevProps.question?.id === nextProps.question?.id
  );
};

export default React.memo(MatchQuestion, arePropsEqual);
